// Utils
import * as d3 from 'd3';

/**
 * Builds a line chart with the given data
 *
 * @param {object[]} data the data for the chart
 * @param {object} options
 * @param {function} [options.x] given d in data, returns the (temporal) x-value
 * @param {function} [options.y] given d in data, returns the (quantitative) y-value
 * @param {function} [options.z] given d in data, returns the (categorical) z-value
 * @param {function} [options.title] given d in data, returns the title text
 * @param {function} [options.defined] for gaps in data
 * @param {function} [options.curve] method of interpolation between points
 * @param {number} [options.marginTop = 0] top margin, in pixels
 * @param {number} [options.marginRight=16] right margin, in pixels
 * @param {number} [options.marginBottom=24] bottom margin, in pixels
 * @param {number} [options.marginLeft=24] left margin, in pixels
 * @param {number} [options.width=640] outer width, in pixels
 * @param {number} [options.height=400] outer height, in pixels
 * @param {function} [options.xType] the x-scale type
 * @param {array} [options.xDomain] [xmin, xmax]
 * @param {number[]} [options.xRange] [left, right]
 * @param {function} [options.yType] the y-scale type
 * @param {array} [options.yDomain] [ymin, ymax]
 * @param {number[]} [options.yRange] [bottom, top]
 * @param {function} [options.yFormat] a format specifier string for the y-axis
 * @param {string} [options.yLabel] a label for the y-axis
 * @param {array} [options.zDomain] array of z-values
 * @param {string|function} [options.color=currentColor] stroke color of line
 * @param {string} [options.strokeLinecap=round] stroke line cap of the line
 * @param {string} [options.strokeLinejoin=round] stroke line join of the line
 * @param {number} [options.strokeWidth=4] stroke width of line, in pixels
 * @param {number} [strokeOpacity] stroke opacity of line
 * @param {string} [mixBlendMode=multiply] blend mode of lines
 * @param {boolean} [options.voronoi=false] show a Voronoi overlay? (for debugging)
 * @param {boolean} [options.withTooltips=true] whether to display a tooltip on the line
 * @returns {SVGElement} the chart in SVG format
 *
 * @copyright 2021 Observable, Inc.
 * @license ISC
 * @see https://observablehq.com/@d3/multi-line-chart
 * @modified SpotMe 12.2022
 */
export function MultilineChart(data, {
    x = ([x]) => x, // given d in data, returns the (temporal) x-value
    y = ([, y]) => y, // given d in data, returns the (quantitative) y-value
    z = () => 1, // given d in data, returns the (categorical) z-value
    title, // given d in data, returns the title text
    defined, // for gaps in data
    curve = d3.curveLinear, // method of interpolation between points
    marginTop = 0, // top margin, in pixels
    marginRight = 0, // right margin, in pixels
    marginBottom = 24, // bottom margin, in pixels
    marginLeft = 24, // left margin, in pixels
    width = 640, // outer width, in pixels
    height = 400, // outer height, in pixels
    xType = d3.scaleUtc, // type of x-scale
    xDomain, // [xmin, xmax]
    xRange = [marginLeft, width - marginRight], // [left, right]
    yType = d3.scaleLinear, // type of y-scale
    yDomain, // [ymin, ymax]
    yRange = [height - marginBottom, marginTop], // [bottom, top]
    yFormat, // a format specifier string for the y-axis
    yLabel, // a label for the y-axis
    zDomain, // array of z-values
    color = 'currentColor', // stroke color of line, as a constant or a function of *z*
    strokeLinecap = 'round', // stroke line cap of line
    strokeLinejoin = 'round', // stroke line join of line
    strokeWidth = 4, // stroke width of line
    strokeOpacity, // stroke opacity of line
    mixBlendMode = 'normal', // blend mode of lines
    withTooltips = true,
    markerLine = false,
    verticalMarkers = [],
    icons = {},
    iconsSize = 32
} = {}) {
    // Compute values.
    const X = d3.map(data, x);
    const Y = d3.map(data, y);
    const Z = d3.map(data.filter(d => !verticalMarkers.includes(z(d))), z);
    const O = d3.map(data, d => d);
    const OV = d3.map(data.filter(d => verticalMarkers.includes(z(d))), d => d);
    if (defined === undefined) defined = (d, i) => !isNaN(X[i]) && !isNaN(Y[i]);
    const D = d3.map(data, defined);

    // Compute default domains, and unique the z-domain.
    if (xDomain === undefined) xDomain = d3.extent(X);
    if (yDomain === undefined) yDomain = [0, d3.max(Y, d => typeof d === 'string' ? +d : d)];
    if (zDomain === undefined) zDomain = Z;
    zDomain = new d3.InternSet(zDomain);

    // Omit any data not present in the z-domain.
    const I = d3.range(X.length).filter(i => zDomain.has(Z[i]));
    const showVerticalsIcons = OV.length && Object.keys(icons).length;

    if (showVerticalsIcons) {
        yRange[1] += iconsSize;
    }

    // Construct scales and axes.
    const xScale = xType(xDomain, xRange);
    const yScale = yType(yDomain, yRange);
    const xAxis = d3.axisBottom(xScale).ticks(width / 80).tickSizeOuter(0);
    const yAxis = d3.axisLeft(yScale).ticks(height / 60, yFormat);

    // Compute titles.
    if (title === undefined) {
        const formatDate = xScale.tickFormat(null, '%b %-d, %Y');
        const formatValue = yScale.tickFormat(100, yFormat);
        title = i => `${Z[i]}\n${formatDate(X[i])}\n${formatValue(Y[i])}`;
    } else {
        const T = title;
        title = i => T(O[i], i, data);
    }

    // Construct a line generator.
    const line = d3.line()
        .defined(i => D[i])
        .curve(curve)
        .x(i => xScale(X[i]))
        .y(i => yScale(Y[i]));

    const svg = d3.create('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', [0, 0, width, height])
        .attr('style', 'max-width: 100%; height: auto; height: intrinsic;')
        .attr('font-family', 'sans-serif')
        .attr('font-size', 12)
        .style('-webkit-tap-highlight-color', 'transparent')
        .style('overflow', 'visible');

    svg.append('g')
        .attr('transform', `translate(0,${height - marginBottom})`)
        .call(xAxis);

    if (OV.length) {
        const verticals = svg.append('g').attr('class', 'lines');
        const verticalIcons = verticals.append('g');

        for (const vMarker of OV) {
            verticals.append('line')
                .attr('transform', 'translate(0,-2)')
                .attr('fill', 'none')
                .attr('class', z(vMarker))
                .attr('stroke', typeof color === 'function' ? color('verticals') : null)
                .attr('stroke-linecap', 'square')
                .attr('stroke-linejoin', strokeLinejoin)
                .attr('stroke-width', 4)
                .attr('stroke-opacity', strokeOpacity)
                .attr('x1', xScale(x(vMarker)))
                .attr('y1', yScale(yDomain[0]))
                .attr('x2', xScale(x(vMarker)))
                .attr('y2', yScale(yDomain[1]));

            if (showVerticalsIcons) {
                verticalIcons.append('rect')
                    .attr('class', z(vMarker))
                    .attr('transform', `translate(${iconsSize / -2},0)`)
                    .attr('x', xScale(x(vMarker)))
                    .attr('y', '0')
                    .attr('width', iconsSize)
                    .attr('height', iconsSize)
                    .attr('rx', iconsSize / 2)
                    .attr('fill', typeof color === 'function' ? color('verticals') : null);

                verticalIcons.append('image')
                    .attr('class', z(vMarker))
                    .attr('transform', `translate(${iconsSize / -4},${iconsSize / 4})`)
                    .attr('href', icons[z(vMarker)])
                    .attr('x', xScale(x(vMarker)))
                    .attr('y', '0')
                    .attr('width', iconsSize / 2)
                    .attr('height', iconsSize / 2);
            }
        }
    }

    svg.append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(yAxis)
        .call(g => g.select('.domain').remove())
        .call(g => g.selectAll('.tick line').clone()
            .attr('x2', width - marginLeft - marginRight)
            .attr('stroke-opacity', 0.1))
        .call(g => g.append('text')
            .attr('x', -marginLeft)
            .attr('y', 10)
            .attr('fill', 'currentColor')
            .attr('text-anchor', 'start')
            .text(yLabel));

    if (markerLine) {
        svg.append('g').append('line')
            .attr('fill', 'none')
            .attr('stroke', typeof color === 'function' ? color('goal') : null)
            .attr('stroke-linecap', strokeLinecap)
            .attr('stroke-linejoin', strokeLinejoin)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', '10 8')
            .attr('stroke-opacity', strokeOpacity)
            .attr('x1', xScale(xDomain[0]))
            .attr('y1', yScale(markerLine))
            .attr('x2', xScale(xDomain[1]))
            .attr('y2', yScale(markerLine))
            .attr('class', 'zeroline');
    }

    const paths = svg.append('g')
        .attr('class', 'lines')
        .attr('fill', 'none')
        .attr('stroke', typeof color === 'string' ? color : null)
        .attr('stroke-linecap', strokeLinecap)
        .attr('stroke-linejoin', strokeLinejoin)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-opacity', strokeOpacity)
        .selectAll('path')
        .data(d3.group(I, i => Z[i]))
        .join('path')
        .style('mix-blend-mode', mixBlendMode)
        .attr('stroke', typeof color === 'function' ? ([z]) => color(z) : null)
        .attr('class', (z) => z[0])
        .attr('d', ([, I]) => line(I));

    let tooltip;
    if (withTooltips) {
        svg
            .on('pointerenter', pointerentered)
            .on('pointermove', pointermoved)
            .on('pointerleave', pointerleft)
            .on('touchstart', event => event.preventDefault(), { passive: true });

        tooltip = svg.append('g')
            .attr('class', 'point-tooltip')
            .style('pointer-events', 'none')
            .attr('opacity', 0);
    }

    /**
     * Returns the closest visible i. If none are visible, returns -1
     * @param {*} event
     * @returns
     */
    function getClosestVisibleI(event) {
        const [xm, ym] = d3.pointer(event);
        const closestI = d3.least(I, i => {
            const path = paths.filter(([z]) => Z[i] === z);
            const isHidden = path.node().getAttribute('opacity') === '0';
            // closest visible point
            return isHidden ? Number.POSITIVE_INFINITY : Math.hypot(xScale(X[i]) - xm, yScale(Y[i]) - ym);
        });
        const path = paths.filter(([z]) => Z[closestI] === z);
        const isHidden = path.node().getAttribute('opacity') === '0';
        return isHidden ? -1 : closestI;

    }

    function pointermoved(event) {
        const i = getClosestVisibleI(event);
        if (i < 0) {
            return;
        }

        const ys = data.filter(d => x(d).toString() === X[i].toString());

        tooltip
            .attr('transform', `translate(${xScale(X[i])},${yScale(Y[i])})`)
            .selectAll('circle')
            .attr('class', (z) => z[0])
            .data(d3.group(I, i => Z[i]))
            .join('circle')
            .attr('r', 6)
            .attr('transform', `translate(0,-${yScale(Y[i])})`)
            .attr('fill', typeof color === 'function' ? ([z]) => color(z) : null)
            .attr('cy', (_, index) => yScale(y(ys[index])));

        const rect = tooltip.selectAll('rect')
            // eslint-disable-next-line no-sparse-arrays
            .data([,])
            .join('rect')
            .attr('rx', 4)
            .attr('ry', 4)
            .attr('fill', '#2d3339');

        const text = tooltip.selectAll('text')
            .attr('fill', 'white')
            // eslint-disable-next-line no-sparse-arrays
            .data([,])
            .join('text')
            .call(text => text
                .selectAll('tspan')
                .data(`${title(i)}`.split(/[\n|\|]/))
                .join('tspan')
                .attr('x', (_, i) => i % 2 === 1 ? '0' : null)
                .attr('y', (_, i) => {
                    if (i === 0) return null;
                    return i % 2 === 1 ? `${(i / 2) * 1.4 + .7}em` : null;
                })
                .attr('font-weight', (_, i) => i % 2 === 1 ? null : 'bold')
                .text(d => d));

        const { y: ycoord, width: w, height: h } = text.node().getBBox();
        text.attr('transform', `translate(${-w / 2},${15 - ycoord})`);
        rect.attr('x', w / -2 - 8)
            .attr('y', 8)
            .attr('height', h + 16)
            .attr('width', w + 16);

        svg.property('value', O[i]).dispatch('input', { bubbles: true });
    }

    function pointerentered(event) {
        const i = getClosestVisibleI(event);
        if (i < 0) {
            return;
        }

        tooltip.style('opacity', '1');
    }

    function pointerleft() {
        tooltip.style('opacity', null);
        svg.node().value = null;
        svg.dispatch('input', { bubbles: true });
    }

    return Object.assign(svg.node(), { value: null });
}
